import Button from "@/components/Button";
import Input from "@/components/Input";
import Table from "@/components/Table";
import HeaderColumn from "@/components/Table/table-header/HeaderColumn";
import { ReactComponent as TRASH_ICON } from "@/assets/icons/trash.svg";
import { ReactComponent as SETTING_ICON } from "@/assets/icons/settings.svg";
import { ReactComponent as LOCK_ICON } from "@/assets/icons/lock-red.svg";
import HeaderColumnTooltip from "@/components/Table/table-header/HeaderColumnTooltip";
import Modal from "@/components/Modal";
import { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import SelectImage from "@/components/SelectImage";
import { ProductUnit } from "@/@core/entity/product.entity";
import uploadFileInteractor from "@/@core/interactor/media/upload-file";
import updateProductUnitInteractor from "@/@core/interactor/stock/update-product-unit.interactor";
import toast, { Toaster } from "react-hot-toast";
import deleteProductUnitInteractor from "@/@core/interactor/stock/delete-product-unit.interactor";
import updateLockStatusInteractor from "@/@core/interactor/stock/update-lock-status.interactor";
import { validateImageResolution } from "@/utils/image";
import useAuth from "@/hooks/useAuth";
interface TableSectionPropTypes {
  productUnits: ProductUnit[];
  reFetchData: () => void;
  changeSerialNumber: Dispatch<SetStateAction<string>>;
  serialNumber: string;
}

let tempIndexProductUnit = -1;

const TableSection: FC<TableSectionPropTypes> = ({
  productUnits,
  reFetchData,
  changeSerialNumber,
  serialNumber,
}) => {
  const [showModalAdd, setShowModalAdd] = useState<boolean>(false);
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false);
  const [normalTxt, setNormalTxt] = useState<string>("");
  const [boldTxt, setBoldTxt] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<any>();
  const [displayImageProductUnit, setDisplayImageProductUnit] =
    useState<any>(null);
  const [isTouchImage, setIsTouchImage] = useState<boolean>(false);
  const [isFetch, setIsFetch] = useState<boolean>(false);
  const [selectedProductUnit, setSelectedProductUnit] = useState<ProductUnit>();
  const [isValidResolution, setIsValidResolution] = useState<boolean>(true);
  const { auth } = useAuth();

  useEffect(() => {
    if (showModalAdd) {
      setIsTouchImage(false);
      setSelectedImage(null);

      setTimeout(() => {
        if (selectedProductUnit) {
          setNormalTxt(selectedProductUnit.normal_text);
          setBoldTxt(selectedProductUnit.bold_text);

          setDisplayImageProductUnit(
            selectedProductUnit.image
              ? `${process.env.REACT_APP_API_URL}/file/${selectedProductUnit.image}`
              : null
          );
        }
      }, 100);
    }
  }, [showModalAdd]);
  
  const isValidImage = useMemo(() => {
    if (showModalAdd) {
      const isValidType =
        selectedImage &&
        (selectedImage?.type === "image/jpeg" ||
          selectedImage?.type === "image/png")
          ? true
          : false;

      if (isValidType) {
        const objectUrl = URL.createObjectURL(selectedImage);
        setDisplayImageProductUnit(objectUrl);
      } else {
        setDisplayImageProductUnit(null);
      }

      return isValidType;
    } else {
      return false;
    }
  }, [selectedImage, showModalAdd]);

  const isValidData = useMemo(() => {
    if (normalTxt && boldTxt) {
      if (selectedImage) {
        return isValidImage;
      }
      return true;
    } else {
      return false;
    }
  }, [normalTxt, boldTxt, isValidImage, selectedImage]);

  const onChangeCustomImage = async (e: ChangeEvent<HTMLInputElement>) => {
    setIsTouchImage(true);
    const files: any = e.target.files;

    if (files && files.length) {
      const isValidRes = await validateImageResolution(files[0], 2048, 2048);
      setIsValidResolution(isValidRes);

      if (isValidRes) {
        setSelectedImage(files[0]);
      }
    }
  };

  const onChangeSerialNumber = async (e: ChangeEvent<HTMLInputElement>) => {
    changeSerialNumber(e?.target.value)
  };

  const onSubmit = async () => {
    setIsFetch(true);
    try {
      if (isValidImage) {
        const uploadImageResp = await uploadFileInteractor(selectedImage);
        const uploadStatus = uploadImageResp?.status;
        const uploadImageId = uploadImageResp?.data?.data?.id;
        const uploadMessage = uploadImageResp?.data?.data?.message;

        if (uploadStatus === 201) {
          updateProductUnit(uploadImageId);
        } else {
          toast.error(uploadMessage);
        }
      } else {
        updateProductUnit();
      }
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsFetch(false);
    }
  };

  const updateProductUnit = async (uploadImageId: any = 0) => {
    try {
      const responseUpdateProductUnit = await updateProductUnitInteractor({
        pathParam: {
          productNumber: `${selectedProductUnit?.product_number}`,
        },
        body: {
          normal_text: normalTxt ? normalTxt : "",
          bold_text: boldTxt ? boldTxt : "",
          media_id: uploadImageId,
        },
      });

      const {
        status,
        data: { message },
      } = responseUpdateProductUnit;
      if (status === 200) {
        setShowModalAdd(false);
        reFetchData();
      } else {
        toast.error(message);
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleError = (error: any) => {
    const { response } = error;
    const {
      data: { message },
    } = response;

    toast.error(message);
  };

  const onDeleteProductUnit = async () => {
    setIsFetch(true);
    try {
      const response = await deleteProductUnitInteractor({
        productNumber: productUnits[tempIndexProductUnit].product_number,
      });

      const { status } = response;

      if (status === 200) {
        setShowModalDelete(false);
        reFetchData();
      }
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsFetch(false);
    }
  };

  const lockOrUnlock = async (productUnit: ProductUnit) => {
    setIsFetch(true);
    try {
      const response = await updateLockStatusInteractor({
        pathParams: {
          productNumber: productUnit.product_number,
        },
        body: {
          is_lock: !productUnit.is_locked,
        },
      });

      const { status } = response;

      if (status === 200) {
        reFetchData();
      }
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsFetch(false);
    }
  };

  return (
    <>
    <div className='table-control-section'>
        <div>
          <label>Search for UID</label>
          <Input
            style={{ width: "360px"}}
            withIcon
            type='search'
            placeholder='UID,UID,UID'
            onChange={onChangeSerialNumber}
            value={serialNumber}
          />
        </div>
        <div className="btn-wrapper">
          <Button
            label="Reset Filter"
            type="secondary"
            size="md"
            onClick={() => {changeSerialNumber("")}}
          />
        </div>
      </div>
      <div className="table-section">
        <Table>
          <thead>
            <tr>
              <HeaderColumnTooltip
                toolTipText="Product Number"
                label="Product Number"
                alignLabel="text-center"
              />
              <HeaderColumn label="UID" alignLabel="text-left" />
              <HeaderColumn label="" alignLabel="text-left" />
            </tr>
          </thead>
          <tbody>
            {productUnits.map((item, index) => (
              <tr key={item.uid}>
                <td width="15%">
                  <div className="text-title item-center">
                    <span>{item.product_number}</span>
                  </div>
                </td>
                <td width="60%">
                  <div className="text">
                    <span>{item.uid}</span>
                  </div>
                </td>
                <td width="20%">
                  <div className="cta-wrapper">
                    {auth.role == "super_admin" ? <button
                      onClick={() => {
                        setShowModalAdd(!showModalAdd);
                        setSelectedProductUnit(item);
                        tempIndexProductUnit = index;
                      }}
                    >
                      <SETTING_ICON />
                    </button> : <></>}
                    <button
                      onClick={() => lockOrUnlock(item)}
                      disabled={isFetch}
                    >
                      <LOCK_ICON
                        className={
                          "lock-icon" + (item.is_locked ? "" : " unlock")
                        }
                      />
                    </button>
                    {auth.role == "super_admin" ? <button
                      onClick={() => {
                        setShowModalDelete(!showModalDelete);
                        tempIndexProductUnit = index;
                      }}
                    >
                      <TRASH_ICON /></button> : <></>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {/* <Pagination activePage={1} totalPage={4} /> */}
      </div>
      {showModalAdd && (
        <Modal
          setIsOpen={() => setShowModalAdd(!showModalAdd)}
          type="customStock"
          withDevider
          title="Add custom text"
          subtitle="Add custom text description"
          content={
            <div className="modal-content">
              <div className="row margin-bt">
                <div className="label">
                  <span>
                    Normal Text<span>*</span>
                  </span>
                </div>
                <div className="input">
                  <Input
                    style={{ width: "400px" }}
                    type="text"
                    withIcon={false}
                    placeholder="e.g. This jersey has been specially crafted for you..."
                    value={normalTxt}
                    onChange={(e) => setNormalTxt(e.target.value)}
                    required
                    disabled={isFetch}
                  />
                </div>
              </div>
              <div className="row">
                <div className="label">
                  <span>
                    Bold Text<span>*</span>
                  </span>
                </div>
                <div className="input">
                  <Input
                    style={{ width: "400px" }}
                    type="text"
                    withIcon={false}
                    placeholder="e.g. Mr. Raja Sapta Oktohari"
                    value={boldTxt}
                    onChange={(e) => setBoldTxt(e.target.value)}
                    required
                    disabled={isFetch}
                  />
                </div>
              </div>
              <div className="divider" />
              {displayImageProductUnit && (
                <img
                  className="custom-bg-img"
                  src={displayImageProductUnit}
                  alt="product-unit-custom-bg"
                />
              )}
              <div className="input">
                <SelectImage
                  onChange={onChangeCustomImage}
                  disabled={isFetch}
                  displayIcon={true}
                  desc="PNG or JPG (2048x2048px)"
                  width="100%"
                  isError={!isValidImage && isTouchImage && !isValidResolution}
                  errorText="Uploaded file must be PNG or JPG, and have maximun 2048x2048 resolution"
                />
              </div>
            </div>
          }
          footer={
            <div className="modal-footer">
              <Button
                style={{ width: "270px" }}
                type="secondary"
                label="Cancel"
                onClick={() => setShowModalAdd(!showModalAdd)}
              />
              <Button
                style={{ width: "270px" }}
                type="primary"
                label={isFetch ? "Uploading" : "Add Source"}
                disabled={!isValidData || isFetch || !isValidResolution}
                onClick={onSubmit}
              />
            </div>
          }
        />
      )}
      {showModalDelete && (
        <div className="delete-warehouse-modal-wrapper">
          <Modal
            header={"header"}
            content={
              <div className="content-delete-warehouse">
                <h2 className="modal-title">Delete Product UID</h2>
                <span className="desc">
                  Are you sure you want to delete this product UID? This action
                  cannot be undone.
                </span>
              </div>
            }
            type="delete"
            setIsOpen={() => setShowModalDelete(!showModalDelete)}
            footer={
              <div className="delete-warehouse-modal-btn-wrapper">
                <Button
                  onClick={() => setShowModalDelete(!showModalDelete)}
                  label="Cancel"
                  type="secondary"
                  size="md"
                />
                <Button
                  onClick={onDeleteProductUnit}
                  label="Delete"
                  type="warning"
                  size="md"
                  disabled={isFetch}
                />
              </div>
            }
          />
        </div>
      )}
      <Toaster />
    </>
  );
};

export default TableSection;
